import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { ChevronDown, ArrowRight, Handshake, Compass, TrendingUp, Target } from "lucide-react";
import { Reveal } from "../Reveal";
import { PROFILE_TYPES } from "../profiles";

const M = process.env.REACT_APP_BACKEND_URL;
const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/8e57cf35-f4fb-40ce-b502-ff37f5224c96/images/6de08a343b71cb8de9cccbb460230129fad2274eebb01bc780d0b398cfd66f20.png";

// Vidéos du film (déplacées ici, à droite du texte) — desktop 16:9, mobile 9:16
const FILM_16 = `${M}/api/media/assets/off_markette_film_v3.mp4`;
const POSTER_16 = `${M}/api/media/assets/story_arrival_d9aa85838b94.jpeg`;
const FILM_9 = `${M}/api/media/assets/off_markette_film_v3_vertical.mp4`;
const POSTER_9 = `${M}/api/media/assets/emma_couple_1b72328bf9c34ea3.jpeg`;

export const JoinContent = ({ advisorName = "", onJoin, accent = "#C19A6B", coverImage = "", headline = "", subtitle = "", ctaImage = HERO_IMG }) => {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 220);
    window.addEventListener("scroll", on, { passive: true });
    on();
    return () => window.removeEventListener("scroll", on);
  }, []);
  const join = (a) => onJoin && onJoin(typeof a === "string" ? a : "");
  const scrollToAvantages = () => {
    const el = document.getElementById("jc-intro");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="jc relative" style={{ ["--jc"]: accent }} data-testid="join-content">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Dancing+Script:wght@700&display=swap');
        .jc .jc-txt{color:var(--jc)}
        .jc .jc-bg{background-color:var(--jc);color:#050505}
        .jc .jc-bg:hover{filter:brightness(1.1)}
        .jc .jc-bd{border-color:var(--jc)}
        .jc .jc-calli{font-family:'Dancing Script',cursive;font-weight:700;text-transform:none !important;letter-spacing:0 !important}
      `}</style>

      {/* Hero */}
      <section className="relative min-h-screen flex items-center justify-center px-6 py-24 overflow-hidden">
        <div className="absolute inset-0">
          <motion.img
            src={coverImage || HERO_IMG} alt="Agence OFF MARKETTE — réseau collaboratif" loading="eager"
            initial={{ scale: 1.08 }} animate={{ scale: 1 }} transition={{ duration: 8, ease: "easeOut" }}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/78 via-[#050505]/62 to-[#050505]" />
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-2/3" style={{ background: "linear-gradient(to top, #050505 0%, rgba(5,5,5,0.88) 32%, transparent 100%)" }} />
          <div className="pointer-events-none absolute inset-0 opacity-[0.10]" style={{ backgroundImage: `radial-gradient(circle at 25% 25%, ${accent}, transparent 45%)` }} />
        </div>
        <div className="max-w-3xl mx-auto text-center relative z-10 mt-28 sm:mt-24" style={{ textShadow: "0 2px 26px rgba(0,0,0,0.8)" }}>
          <Reveal delay={0.05}>
            <h1 className="text-xl sm:text-2xl lg:text-[1.95rem] font-bold text-white tracking-tight leading-tight mx-auto">
              {headline ? headline : (subtitle ? (<>{t("join.hero_title_alt_1")}<br />{t("join.hero_title_alt_2")}</>) : (<>{t("join.hero_title_default")}</>))}
            </h1>
          </Reveal>
          {subtitle ? (
            <Reveal delay={0.2}>
              <p className="text-sm md:text-base text-zinc-200 mt-4 max-w-2xl mx-auto leading-relaxed font-light">{subtitle}</p>
            </Reveal>
          ) : !headline ? (
            <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-x-10 gap-y-8 sm:gap-x-16 max-w-md sm:max-w-3xl mx-auto">
              {[
                { label: t("join.labels.0"), Icon: Compass },
                { label: t("join.labels.1"), Icon: Handshake },
                { label: t("join.labels.2"), Icon: TrendingUp },
                { label: t("join.labels.3"), Icon: Target },
              ].map((p) => (
                <div key={p.label} className="flex flex-col items-center gap-3">
                  <p.Icon className="text-white w-7 h-7 sm:w-8 sm:h-8" strokeWidth={2.4} />
                  <span className="jc-calli text-white text-2xl sm:text-3xl leading-none">{p.label}</span>
                </div>
              ))}
            </div>
          ) : null}
          <p className="text-[11px] uppercase tracking-[0.25em] text-zinc-200 mt-9 mb-3" style={{ textShadow: "0 2px 12px rgba(0,0,0,0.9)" }}>{t("join.profile_q")}</p>
          <div className="flex flex-wrap items-center justify-center gap-1.5 sm:gap-2 max-w-2xl mx-auto">
            {PROFILE_TYPES.filter((a) => a !== "Autre").map((a, i) => (
              <button key={a}
                onClick={() => join(a)}
                data-testid={`join-audience-${i}`}
                className="text-[10px] sm:text-[11px] uppercase tracking-wider text-white border border-white/25 hover:border-[#C19A6B] bg-white/10 hover:bg-[#C19A6B]/25 hover:-translate-y-0.5 backdrop-blur-sm px-2.5 sm:px-3 py-1.5 sm:py-2 shadow-lg shadow-black/20 transition-all duration-200 cursor-pointer">
                {a}
              </button>
            ))}
          </div>
        </div>
        <motion.button
          type="button"
          onClick={scrollToAvantages}
          data-testid="join-discover-advantages"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 0.8 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-2.5 group cursor-pointer"
          aria-label="Découvrir les avantages du réseau"
        >
          <span className="text-[11px] uppercase tracking-[0.22em] text-white/85 group-hover:text-[#C19A6B] transition-colors duration-300" style={{ textShadow: "0 2px 12px rgba(0,0,0,0.9)" }}>{t("join.discover")}</span>
          <span className="relative flex items-center justify-center w-6 h-7">
            <motion.span animate={{ y: [0, 7, 0], opacity: [0.35, 1, 0.35] }} transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }} className="absolute top-0 text-[#C19A6B]"><ChevronDown className="w-5 h-5" strokeWidth={2.2} /></motion.span>
            <motion.span animate={{ y: [0, 7, 0], opacity: [0.15, 0.75, 0.15] }} transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut", delay: 0.22 }} className="absolute top-2.5 text-[#C19A6B]"><ChevronDown className="w-5 h-5" strokeWidth={2.2} /></motion.span>
          </span>
        </motion.button>
      </section>

      {/* Bloc 2 colonnes : GAUCHE = pourquoi rejoindre (simplifié) · DROITE = le film */}
      <section id="jc-intro" className="px-6 py-20 md:py-28 border-b border-white/5">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-10 md:gap-16 items-center">
          {/* Colonne gauche */}
          <div>
            <Reveal delay={0.05}>
              <h2 className="calli text-4xl sm:text-5xl font-bold text-white leading-tight">
                Pourquoi rejoindre <span className="jc-txt">Off Markette</span> ?
              </h2>
            </Reveal>
            <Reveal delay={0.12}>
              <p className="text-[15px] text-zinc-300 font-light leading-relaxed mt-5">
                Le marché immobilier change vite : les acquéreurs cherchent des biens plus rares et mieux valorisés, les vendeurs attendent un accompagnement plus performant.
              </p>
            </Reveal>
            <Reveal delay={0.18}>
              <p className="text-[15px] text-zinc-300 font-light leading-relaxed mt-3">
                Trop de professionnels avancent encore seuls. Off Markette réunit un <span className="text-white">réseau national augmenté par l'IA</span> où chacun garde son identité, ses clients et son indépendance — tout en profitant de la force du collectif.
              </p>
            </Reveal>
            <Reveal delay={0.5}>
              <button onClick={() => join()} data-testid="join-why-cta" className="jc-bg mt-9 inline-flex items-center gap-2 font-semibold uppercase tracking-wider text-sm px-7 py-3.5 rounded-lg transition-all">
                {t("join.cta_btn")} <ArrowRight className="w-4 h-4" />
              </button>
            </Reveal>
          </div>

          {/* Colonne droite : vidéo — desktop 16:9 / mobile 9:16 */}
          <Reveal delay={0.15}>
            <div className="relative">
              <div className="absolute -inset-3 bg-[#C19A6B]/10 blur-2xl rounded-3xl pointer-events-none" aria-hidden />
              <div className="relative flex items-center justify-center gap-2 mb-3">
                <span className="text-[11px] uppercase tracking-[0.25em] jc-txt font-semibold">Le film Off Markette</span>
                <span className="text-[9.5px] uppercase tracking-wider bg-[#C19A6B] text-black rounded-full px-2 py-0.5 font-bold">Nouveau</span>
              </div>
              {/* Desktop 16:9 */}
              <div className="hidden md:block relative rounded-2xl overflow-hidden border border-[#C19A6B]/35 shadow-[0_25px_60px_-15px_rgba(0,0,0,0.7)] bg-black">
                <video
                  data-testid="join-film-desktop"
                  className="w-full aspect-video object-cover bg-black"
                  poster={POSTER_16} controls playsInline preload="metadata"
                >
                  <source src={FILM_16} type="video/mp4" />
                </video>
              </div>
              {/* Mobile 9:16 */}
              <div className="md:hidden relative mx-auto max-w-[300px] rounded-2xl overflow-hidden border border-[#C19A6B]/35 shadow-[0_20px_50px_-15px_rgba(0,0,0,0.7)] bg-black">
                <video
                  data-testid="join-film-mobile"
                  className="w-full aspect-[9/16] object-cover bg-black"
                  poster={POSTER_9} controls playsInline preload="metadata"
                >
                  <source src={FILM_9} type="video/mp4" />
                </video>
              </div>
              <p className="relative text-center text-[12px] text-zinc-500 mt-3">De la rencontre à la remise des clés.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Bouton flottant — apparaît au défilement */}
      <button onClick={() => join()} data-testid="join-floating" className={`jc-bg fixed bottom-6 right-6 z-[90] inline-flex items-center gap-2 font-medium uppercase tracking-wider text-xs md:text-sm px-5 md:px-6 py-3.5 shadow-2xl shadow-black/40 transition-all duration-500 ${scrolled ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-6 pointer-events-none"}`}>
        <Handshake className="w-4 h-4" /> {t("join.floating")}
      </button>
    </div>
  );
};

export default JoinContent;
